<template>
  <div>
    <el-row>
      <el-col :span="5" :xs="24" style="padding-right: 10px">
        <AdTypeSelect
          v-model="adType"
          :with-empty="false"
          @update:modelValue="update"
        />
      </el-col>
      <el-col :span="19" :xs="24">
        <el-alert
          title="Displayed data is based on yesterday reports"
          type="success"
          :closable="false"
        />
      </el-col>
    </el-row>
    <DailyTable :items="items" :columns="COLUMNS" @download="download" />
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onUnmounted } from 'vue'
import DailyTable from './DailyTable.vue'
import AdTypeSelect from '@/components/Select/AdTypeSelect.vue'
import { exportJson2Excel, exportCSVFile } from '@/utils/excel'
import { formatJson } from '@/utils'
import NaRespondedJob from '@/utils/responded_job'
import { ElMessage } from 'element-plus'
import { AdType } from '@/api/types'
import { IRespondedJob } from '@/interfaces/respondedJobs'

import useRouter from '@/hooks/useRouter'

const COLUMNS: Record<string, string> = {
  title: 'Campaign',
  impressions: 'Impresssions',
  cpm: 'My eCPM',
  limits: 'My limits',
  completion: '% of Completion',
  volumes: 'Market volume',
  win_percent: 'Won %',
  avg_cpm: 'Marketplace eCPM',
  lost_impressions: 'Impressions lost'
}

export default defineComponent({
  name: 'TabDaily',
  components: {
    DailyTable,
    AdTypeSelect
  },
  props: {
    jid: {
      type: String,
      default: ''
    }
  },
  setup(props, context) {
    const adType = ref(AdType.Popunder)

    const items = ref([])

    const { router } = useRouter()

    const changeRoute = (id: string) => {
      router.replace({
        name: 'reports-tab-result',
        params: { tab: 'traffic', jid: id }
      })
    }

    const showError = (error: string) => {
      context.emit('update:modelValue', false)
      ElMessage.error(error)
    }

    const onExpired = () => {
      context.emit('update:modelValue', false)
      router.replace({ name: 'reports-tab', params: { tab: 'traffic' } })
    }

    const job = new NaRespondedJob(showError, changeRoute, onExpired)

    const fillJobData = (job: IRespondedJob) => {
      context.emit('update:modelValue', false)
      items.value = job.attributes.result || []
      adType.value = job.attributes.args.ad_type
    }

    if (props.jid) {
      context.emit('update:modelValue', true)
      job.setAfterJobCallback(fillJobData)
      job.checkJobWithInterval(props.jid)
    }

    const update = () => {
      context.emit('update:modelValue', true)
      const payload = {
        ad_type: adType.value
      }
      job.runRespondedJob('type_advert_daily_report', payload, fillJobData)
    }

    const download = (format: string) => {
      const data = formatJson(Object.keys(COLUMNS), items.value)
      if (format === 'csv') {
        exportCSVFile(Object.keys(COLUMNS), data)
      } else {
        exportJson2Excel(Object.keys(COLUMNS), data)
      }
    }

    if (!props.jid) {
      update()
    }

    onUnmounted(() => {
      job.destroy()
    })

    return {
      adType,
      items,
      download,
      update,
      COLUMNS
    }
  }
})
</script>

<style lang="scss" scoped>
.el-select {
  width: 100%;
}
.el-alert {
  padding: 8px 16px 15px 16px;
}
</style>
